/**
 * US Citrus Quiz Logic Engine
 *
 * Why this matters: The overhaul replaces the old tag-only personality builder
 * with a spec-driven quiz engine. This file now owns the full question flow,
 * scoring rules, deterministic result content, recommendation grouping, and
 * submit payload generation so the UI can stay dumb and render resolved data.
 */

import { CitrusTree, citrusTrees } from '../data/citrusTrees';
import {
  CITRUS_BECAUSE_BY_TAG,
  CITRUS_BECAUSE_FALLBACK,
  CITRUS_BECAUSE_PRIORITY,
  citrusBecauseLine,
  citrusBecauseLineStacked,
  type CitrusBecauseTemplate,
} from '../data/citrusQuizUxCopy';

export type LocationTag = 'Indoor-Patio' | 'In-Ground';
export type DifficultyTag = 'Beginner' | 'Advanced';
export type FlavorTag = 'Sweet' | 'Sour' | 'Balanced';
export type UsageTag = 'Snack' | 'Juice' | 'Cook' | 'Aromatic';
export type VibeTag = 'Classic' | 'Exotic' | 'Ornamental' | 'Healer';
export type PreferenceTag = 'Seedless' | 'EdibleRind' | 'Cultural';
export type QuizTag = LocationTag | DifficultyTag | FlavorTag | UsageTag | VibeTag | PreferenceTag;

export type ResultProfileId =
  | 'sweet-snacker'
  | 'culinary-exotic'
  | 'kitchen-essential'
  | 'morning-juice-bar'
  | 'patio-showpiece'
  | 'holistic-healer';

export type ExperienceValue = 'Beginner' | 'Intermediate' | 'Advanced';
export type QuestionType = 'single' | 'multiple' | 'text' | 'screen' | 'loading';

export interface QuizAnswer {
  questionId: string;
  selectedOptions: string[];
  tags: QuizTag[];
}

export interface QuizState {
  currentStep: number;
  userName: string;
  zipCode: string;
  answers: QuizAnswer[];
  accumulatedTags: QuizTag[];
  email: string;
  isComplete: boolean;
}

export interface QuizOption {
  id: string;
  label: string;
  emoji?: string;
  tags: QuizTag[];
  description?: string;
  profileBoosts?: Partial<Record<ResultProfileId, number>>;
}

export interface QuizQuestion {
  id: string;
  type: QuestionType;
  eyebrow?: string;
  caption?: string;
  title: string;
  subtitle?: string;
  description?: string;
  helperText?: string;
  buttonLabel?: string;
  options?: QuizOption[];
  placeholder?: string;
  allowMultiple?: boolean;
  minSelections?: number;
  maxSelections?: number;
  exclusiveOptionIds?: string[];
  countsTowardProgress?: boolean;
  autoAdvanceMs?: number;
  /**
   * UX-3: single-select questions advance right after the tap (with the
   * micro-acknowledgment) and render no Continue button.
   */
  autoAdvance?: boolean;
}

export interface ResultProfile {
  id: ResultProfileId;
  name: string;
  headline: string;
  description: string;
  emoji: string;
}

export interface TreeScore {
  tree: CitrusTree;
  score: number;
  matchReasons: string[];
  personalizedReason: string;
}

export interface ResolvedQuizResult {
  profile: ResultProfile;
  profileScores: Record<ResultProfileId, number>;
  identityScores: Record<ResultProfileId, number>;
  headline: string;
  archetype: string;
  coreReading: string;
  superpower: string;
  keyInsight: string;
  blindSpot: string;
  spiritCitrus: string;
  topMatches: TreeScore[];
  moreMatches: TreeScore[];
  recommendations: TreeScore[];
  location: LocationTag;
  flavor: FlavorTag;
  experience: ExperienceValue;
}

export interface PersonalityReading {
  archetype: string;
  rarityPercentage: number | null;
  coreReading: string;
  strengthLine: string;
  keyInsightLine: string;
  blindSpotLine: string;
  citrusSpirit: string;
}

/** Ibrahim's exact Klaviyo profile keys for pre-written email merge tags. */
export interface QuizEmailContent {
  citrus_quiz_archetype_paragraph: string;
  citrus_quiz_profile_description: string;
  citrus_quiz_headline: string;
  citrus_quiz_superpower: string;
  citrus_quiz_key_insight: string;
  citrus_quiz_blind_spot: string;
  citrus_quiz_spirit_citrus: string;
  citrus_tree_1_snippet: string;
  citrus_tree_2_snippet: string;
  citrus_tree_3_snippet: string;
  citrus_tree_1_snippet_long: string;
  citrus_tree_2_snippet_long: string;
  citrus_tree_3_snippet_long: string;
  citrus_tree_4_snippet: string;
  citrus_tree_5_snippet: string;
  citrus_tree_6_snippet: string;
}

export interface KlaviyoQuizPayload {
  email: string;
  firstName: string;
  zipCode: string;
  quizProfile: string;
  quizProfileId: ResultProfileId;
  quizArchetype: string;
  quizLocation: LocationTag;
  quizFlavor: FlavorTag;
  quizExperience: ExperienceValue;
  recommendedTrees: {
    id: string;
    name: string;
    productUrl: string;
    imageUrl: string;
  }[];
  tags: string[];
  answers: Record<string, string[]>;
  emailContent: QuizEmailContent;
}

type TreeSnippetResolvers = {
  getTreeShortSnippet: (treeId: string, treeName: string) => string;
  getTreeLongSnippet: (treeId: string, treeName: string) => string;
};

/**
 * Lazy-load Klaviyo-only tree snippet copy when submit builds the payload.
 * Why this matters: long snippet copy is large and not needed during quiz browsing,
 * so dynamic import keeps it out of the initial quiz bundle.
 */
const loadTreeSnippetResolvers = async (): Promise<TreeSnippetResolvers> => import('../data/treeSnippets');

const PROFILE_ORDER: ResultProfileId[] = [
  'sweet-snacker',
  'culinary-exotic',
  'kitchen-essential',
  'morning-juice-bar',
  'patio-showpiece',
  'holistic-healer',
];

const PROFILE_FALLBACK_BY_FLAVOR: Record<FlavorTag, ResultProfileId> = {
  Sweet: 'sweet-snacker',
  Sour: 'kitchen-essential',
  Balanced: 'culinary-exotic',
};

const PROFILE_BASE_WEIGHTS: Record<UsageTag | VibeTag, Partial<Record<ResultProfileId, number>>> = {
  Snack: { 'sweet-snacker': 3 },
  Juice: { 'sweet-snacker': 1, 'kitchen-essential': 1, 'morning-juice-bar': 3 },
  Cook: { 'culinary-exotic': 2, 'kitchen-essential': 3 },
  Aromatic: { 'culinary-exotic': 1, 'patio-showpiece': 2, 'holistic-healer': 2 },
  Classic: { 'sweet-snacker': 1, 'kitchen-essential': 2 },
  Exotic: { 'culinary-exotic': 3 },
  Ornamental: { 'patio-showpiece': 4 },
  Healer: { 'holistic-healer': 4 },
};

const ARCHETYPE_BY_PRIORITY: Partial<Record<QuizTag, string>> = {
  Healer: 'The Plant Whisperer',
  Exotic: 'The Trailblazer',
  Ornamental: 'The Natural Designer',
  Cook: 'The Home Chef',
  Juice: 'The Morning Ritual',
  Snack: 'The Everyday Epicurean',
  Advanced: 'The Seasoned Grower',
  Beginner: 'The First-Timer',
  'In-Ground': 'The Legacy Builder',
  'Indoor-Patio': 'The Urban Cultivator',
};

const ARCHETYPE_PRIORITY: QuizTag[] = [
  'Healer',
  'Exotic',
  'Ornamental',
  'Cook',
  'Juice',
  'Snack',
  'Advanced',
  'Beginner',
  'In-Ground',
  'Indoor-Patio',
];

const SUPERPOWERS_BY_ARCHETYPE: Record<string, string> = {
  'The Plant Whisperer': "You listen to what nature is actually saying.",
  'The Trailblazer': "You make the impossible feel inevitable.",
  'The Natural Designer': "Your garden does more than grow — it restores.",
  'The Home Chef': "You transform ordinary into extraordinary.",
  'The Morning Ritual': "You create rituals that anchor your life.",
  'The Everyday Epicurean': "You see the full picture when everyone else is looking at one branch.",
  'The Seasoned Grower': "You know what works and what doesn't — and you're not afraid to say so.",
  'The First-Timer': "You started before you felt ready. That's how every good grower begins.",
  'The Urban Cultivator': "You turned a small space into a working garden. That's not compromise — that's creativity.",
  'The Legacy Builder': "You build for generations, not just seasons.",
};

const KEY_INSIGHTS_BY_PROFILE: Record<ResultProfileId, string[]> = {
  'sweet-snacker': [
    'The best snack is the one that was growing ten minutes ago.',
    "You know the difference between grocery-store sweet and tree-ripe sweet. They're not the same.",
    'The simplest pleasures often bring the deepest satisfaction.',
    'Why complicate something that works perfectly as-is?',
  ],
  'culinary-exotic': [
    "You hear 'you can't grow that here' as a personal challenge.",
    'Normal was never going to be enough for you.',
    'The ingredient that changes everything usually starts with curiosity.',
    'Your kitchen gets more interesting the moment the ordinary stops being enough.',
  ],
  'kitchen-essential': [
    'A dish without fresh citrus is just... incomplete.',
    "You build on what works instead of chasing what's new.",
    'The best kitchen upgrades are the ones you use every single day.',
    'Fresh citrus is one of those ingredients that quietly makes everything better.',
  ],
  'morning-juice-bar': [
    'The gap between store-bought and tree-to-glass is bigger than you expected.',
    "Once you've squeezed your own, the carton feels like a compromise.",
    'How you start your morning sets the tone for everything else.',
    'That first fresh squeeze turns routine into ritual.',
  ],
  'patio-showpiece': [
    'A single tree in the right pot can make a whole patio feel intentional.',
    `The best compliment isn't "nice plant." It's "wait, is that real fruit?"`,
    'A beautiful space changes how you think, not just how things look.',
    'Some people see plants. You see living sculpture.',
  ],
  'holistic-healer': [
    'The first cup of citrus tea you make from your own tree changes the whole ritual.',
    "Growing it yourself means knowing exactly what's in it — and what isn't.",
    "Plants have taught humans for thousands of years. You're still listening.",
    'Wellness starts in the garden before it reaches the medicine cabinet.',
  ],
};

const RESULT_PROFILES: Record<ResultProfileId, Omit<ResultProfile, 'headline'>> = {
  'sweet-snacker': {
    id: 'sweet-snacker',
    name: 'Sweet Snacker',
    description: "Your ideal tree makes fresh fruit feel easy, immediate, and worth walking outside for.",
    emoji: '🍊',
  },
  'culinary-exotic': {
    id: 'culinary-exotic',
    name: 'Culinary Exotic',
    description: "You're after rare citrus with flavor, fragrance, and enough personality to change a whole meal.",
    emoji: '🗺️',
  },
  'kitchen-essential': {
    id: 'kitchen-essential',
    name: 'Kitchen Essential',
    description: 'You want the trees that quietly upgrade every dish, cocktail, and weeknight recipe.',
    emoji: '👨‍🍳',
  },
  'morning-juice-bar': {
    id: 'morning-juice-bar',
    name: 'Morning Juice Bar',
    description: 'You want a tree that pays you back in fresh-squeezed mornings and serious juice output.',
    emoji: '🥤',
  },
  'patio-showpiece': {
    id: 'patio-showpiece',
    name: 'Patio Showpiece',
    description: 'You want a citrus tree that earns compliments first and harvests second without sacrificing either.',
    emoji: '✨',
  },
  'holistic-healer': {
    id: 'holistic-healer',
    name: 'Holistic Healer',
    description: 'You see citrus as food, fragrance, ritual, and natural wellness all in one living plant.',
    emoji: '🧘',
  },
};

const HEADLINE_VARIANTS: Record<
  ResultProfileId,
  { default: string; variantA: string; variantB: string }
> = {
  'sweet-snacker': {
    default: "You know the best fruit doesn't need a recipe.",
    variantA: 'Grab. Peel. Done. That’s your kind of harvest.',
    variantB: 'Simple pleasures, straight off the branch.',
  },
  'culinary-exotic': {
    default: "Your garden grows things most people can't find in a store.",
    variantA: "You don't follow recipes. You create them.",
    variantB: 'Your kitchen just found its secret weapon.',
  },
  'kitchen-essential': {
    default: 'No kitchen runs right without fresh citrus.',
    variantA: "You're the person who always has a lemon on hand.",
    variantB: "The chef's real best friend grows outside.",
  },
  'patio-showpiece': {
    default: 'Your space just got a living centerpiece.',
    variantA: 'Beautiful to look at. Even better to pick from.',
    variantB: "The tree your yard's been missing.",
  },
  'holistic-healer': {
    default: "Nature's pharmacy, growing in your own space.",
    variantA: 'A living remedy on your windowsill.',
    variantB: 'Your yard grows more than fruit. It grows remedies.',
  },
  'morning-juice-bar': {
    default: 'One tree. Endless fresh-squeezed mornings.',
    variantA: 'Your mornings just got an upgrade.',
    variantB: 'Fresh-squeezed and fresh-picked. Every day.',
  },
};

const CORE_READINGS: Record<ResultProfileId, Record<LocationTag, string>> = {
  'sweet-snacker': {
    'Indoor-Patio':
      "You figured something out that most people miss: you don't need a big backyard to have fresh fruit at arm's reach. A sunny corner, a good pot, and a little attention — that's your whole system. And it works.\n\nYou keep things simple because simple is underrated. While everyone else is complicating things, you're peeling a mandarin and enjoying the moment. That's not lazy. That's wisdom.",
    'In-Ground':
      "There's something satisfying about walking into your own yard and picking fruit off a tree you planted. No trip to the store, no checking labels — just reaching up and grabbing the sweetest thing in the garden.\n\nYou don't need it to be fancy. You need it to be good. And when a tree delivers fruit that's perfectly sweet with zero effort at the table, that's your kind of payoff.",
  },
  'culinary-exotic': {
    'Indoor-Patio':
      "You're growing things most people have never even tasted — and you're doing it from your patio. That takes equal parts curiosity and resourcefulness. You don't wait for the specialty store to carry what you want. You grow it.\n\nYour kitchen isn't where you follow recipes. It's where you experiment. A yuzu vinaigrette, a kumquat glaze, a blood orange reduction — you're the reason your dinner guests always ask \"what IS that?\"",
    'In-Ground':
      "You planted something most of your neighbors can't even pronounce — and you're proud of that. Your yard isn't just a yard. It's a collection.\n\nYou cook the way you garden: with intention and a little bit of audacity. The best meals aren't safe. They're surprising. And the best ingredients don't come from an aisle — they come from trees you chose on purpose.",
  },
  'kitchen-essential': {
    'Indoor-Patio':
      "Every good kitchen has a lemon close by. You know this because you're the person who actually uses fresh citrus instead of the plastic bottle in the fridge door. And now you're growing it yourself — even with limited space.\n\nYou're practical, not boring. There's a difference. The right lemon tree or lime tree within reach of your kitchen is one of those small upgrades that changes how everything tastes.",
    'In-Ground':
      "A kitchen that runs out of lemons is a kitchen in trouble — and yours never does. You planted a solution, not a decoration.\n\nYou're the person who knows that a squeeze of fresh lime makes the dish. That a strip of zest changes the whole cocktail. Other people Google recipes. You just walk outside.",
  },
  'patio-showpiece': {
    'Indoor-Patio':
      "Your space tells a story, and you chose a tree that earns its spot. Not just because it produces fruit — but because it's genuinely beautiful. The dark green leaves, the bright fruit, the blossoms that fill the room — it all matters to you.\n\nMost people pick plants for function. You pick them for presence. And somehow, you always end up with something that does both.",
    'In-Ground':
      "Your yard is the one people comment on. Not because you hired a landscaper, but because you have taste. A citrus tree in the ground isn't just a plant to you — it's a centerpiece.\n\nYou understand that a well-placed tree changes the whole feel of a space. The color of the fruit against green leaves, the fragrance on a warm evening — you didn't stumble into that. You designed it.",
  },
  'holistic-healer': {
    'Indoor-Patio':
      `You see a citrus tree and you don't just think "fruit." You think vitamin C, essential oils, tea, and a living thing that cleans the air. Growing your own isn't a hobby for you — it's part of how you take care of yourself and the people around you.\n\nThere's something about having a living remedy on your windowsill. You trust what you can grow more than what you can buy.`,
    'In-Ground':
      "You planted your tree knowing that the fruit was only part of the point. The leaves have a purpose. The peel has a purpose. Even the blossoms serve you — calming, fragrant, medicinal.\n\nYou approach your garden the way you approach your health: naturally, intentionally, and with more knowledge than most people realize. Your yard isn't just growing food. It's growing medicine.",
  },
  'morning-juice-bar': {
    'Indoor-Patio':
      "Your morning has a ritual, and it starts with something fresh. You didn't buy a citrus tree to look at — you bought it because store-bought juice stopped cutting it. Now you squeeze your own, and the difference isn't subtle.\n\nYou're the kind of person who'd rather invest in the source than keep buying the product. One tree, endless mornings. The math made sense and the flavor sealed it.",
    'In-Ground':
      "You walk out your back door in the morning, pick what you need, and twenty minutes later there's a glass of fresh juice on the counter. That's not a fantasy — that's your Tuesday.\n\nMost people don't think about where their juice comes from. You do. And once you tasted the difference between fresh-off-the-tree and pasteurized-from-concentrate, there was no going back.",
  },
};

const BLIND_SPOTS: Record<ResultProfileId, Record<string, string>> = {
  'sweet-snacker': {
    Any: "Don't skip the sour varieties forever. A little tartness in the mix makes you appreciate the sweet ones even more.",
    'The Trailblazer': 'You might outgrow the easy-peel mandarins faster than you think. Leave room in your collection for something that surprises you.',
    'The Morning Ritual': 'You might outgrow the easy-peel mandarins faster than you think. Leave room in your collection for something that surprises you.',
  },
  'culinary-exotic': {
    Any: 'Not every meal needs a showstopper ingredient. Sometimes a classic lemon does the job better than the rarest variety on the shelf.',
    'The Home Chef': 'Your guests love the wow factor, but don’t forget to grow something reliable too. The tree you reach for daily matters more than the one you show off monthly.',
    'The Seasoned Grower': 'Your guests love the wow factor, but don’t forget to grow something reliable too. The tree you reach for daily matters more than the one you show off monthly.',
  },
  'kitchen-essential': {
    Any: "You rely on what works — but your garden has room for one wildcard. The tree you didn't plan on might become the one you use most.",
    'The Plant Whisperer': "Efficiency is a strength, but don't let it keep you from enjoying the slower parts of growing. Not everything needs to earn its spot immediately.",
    'The Legacy Builder': "Efficiency is a strength, but don't let it keep you from enjoying the slower parts of growing. Not everything needs to earn its spot immediately.",
  },
  'patio-showpiece': {
    Any: 'A beautiful tree that never fruits is still just a decoration. Make sure at least one of your picks is as productive as it is pretty.',
    'The Natural Designer': "You design spaces so well that people forget there's work behind it. Don't forget to enjoy the garden yourself — not just how it looks to others.",
  },
  'holistic-healer': {
    Any: "Not everything needs a purpose to earn its place. Sometimes a tree is just a tree, and the fruit is just delicious. That's enough.",
    'The Plant Whisperer': "You research deeply, which is a gift. Just don't let the research phase last longer than the planting phase. The best remedy is the one that's actually growing.",
  },
  'morning-juice-bar': {
    Any: 'Fresh juice is the goal, but a tree needs 1-2 seasons to really produce. Be patient with the early harvests — the volume comes.',
    'The Morning Ritual': "You've built a great routine. Just remember that your tree's schedule doesn't always match yours. Citrus fruits when it's ready, not when your glass is empty.",
    'The Everyday Epicurean': "You've built a great routine. Just remember that your tree's schedule doesn't always match yours. Citrus fruits when it's ready, not when your glass is empty.",
  },
};

const PROFILE_NAME_BY_IDENTITY_OPTION: Record<string, Partial<Record<ResultProfileId, number>>> = {
  family: { 'sweet-snacker': 3, 'morning-juice-bar': 2 },
  kitchen: { 'kitchen-essential': 3, 'culinary-exotic': 2 },
  rare: { 'culinary-exotic': 3, 'patio-showpiece': 1 },
  wellness: { 'holistic-healer': 3, 'kitchen-essential': 1 },
  beauty: { 'patio-showpiece': 3, 'holistic-healer': 1 },
  morning: { 'morning-juice-bar': 3, 'sweet-snacker': 2 },
};

export const initialQuizState: QuizState = {
  currentStep: 0,
  userName: '',
  zipCode: '',
  answers: [],
  accumulatedTags: [],
  email: '',
  isComplete: false,
};

export const quizQuestions: QuizQuestion[] = [
  {
    id: 'location',
    type: 'single',
    autoAdvance: true,
    // Why this matters: intro lives on Q1 so users land on tappable cards — no standalone gate.
    eyebrow: 'Seven quick questions',
    subtitle:
      "Let's find the citrus tree that was made for you. A free grow guide and a surprise are waiting at the end.",
    title: 'Where will your trees live?',
    options: [
      {
        id: 'strictly-indoors',
        label: 'Strictly Indoors',
        emoji: '☀️',
        description: "A little sunshine and the right pot — that's all it takes.",
        tags: ['Indoor-Patio'],
      },
      {
        id: 'patio-container',
        label: 'Patio / Container',
        emoji: '🪴',
        description: 'The best seat in the house, but outside.',
        tags: ['Indoor-Patio'],
      },
      {
        id: 'in-ground',
        label: 'In the Ground',
        emoji: '🌳',
        description: 'Nothing beats walking out your back door and picking fresh fruit.',
        tags: ['In-Ground'],
      },
    ],
    countsTowardProgress: true,
  },
  {
    id: 'experience',
    type: 'single',
    autoAdvance: true,
    eyebrow: "Be honest — we won't judge.",
    title: 'How much growing experience do you have?',
    options: [
      {
        id: 'beginner',
        label: "I'm just getting started",
        emoji: '🌱',
        description: "Everyone's first tree was their first tree.",
        tags: ['Beginner'],
      },
      {
        id: 'intermediate',
        label: 'I know a few things',
        emoji: '🪴',
        description: "You've kept something alive. That counts.",
        tags: [],
      },
      {
        id: 'advanced',
        label: "I've been at this a while",
        emoji: '🧑‍🌾',
        description: "You don't need us to explain drainage.",
        tags: ['Advanced'],
      },
    ],
    countsTowardProgress: true,
  },
  {
    id: 'transition',
    type: 'screen',
    caption: '',
    title: 'Nice. Now for the fun part.',
    description: 'Keep going. A free grow guide and a surprise are waiting at the end.',
    subtitle: '',
    countsTowardProgress: false,
  },
  {
    id: 'flavor',
    type: 'single',
    autoAdvance: true,
    eyebrow: 'Close your eyes and imagine biting into your perfect citrus.',
    title: 'What flavor are you craving?',
    options: [
      {
        id: 'sweet',
        label: 'Pure sweetness',
        emoji: '🍊',
        description: 'Mandarins you can peel in 3 seconds. Oranges that taste like candy.',
        tags: ['Sweet'],
      },
      {
        id: 'sour',
        label: 'Sour power',
        emoji: '🍋',
        description: 'The kind of lemon that makes your whole kitchen smell alive.',
        tags: ['Sour'],
      },
      {
        id: 'balanced',
        label: 'A little of both',
        emoji: '✨',
        description: 'Meyer lemons, anyone? Sweet with a bite.',
        tags: ['Balanced'],
      },
    ],
    countsTowardProgress: true,
  },
  {
    id: 'usage',
    type: 'multiple',
    eyebrow: "Imagine it's harvest day. What are you doing?",
    title: 'How will you use your harvest?',
    options: [
      {
        id: 'snack',
        label: 'Peeling and eating right off the tree',
        emoji: '🍊',
        description: 'The 30-second snack.',
        tags: ['Snack'],
      },
      {
        id: 'juice',
        label: 'Squeezing fresh juice',
        emoji: '🥤',
        description: 'One tree can produce 100+ lemons a year.',
        tags: ['Juice'],
      },
      {
        id: 'cook',
        label: 'Cooking, zesting, making cocktails',
        emoji: '🍸',
        description: 'The ingredient that changes everything.',
        tags: ['Cook'],
      },
      {
        id: 'aromatic',
        label: 'Enjoying the fragrance and beauty',
        emoji: '🌼',
        description: 'Citrus blossoms smell like a vacation.',
        tags: ['Aromatic'],
      },
    ],
    allowMultiple: true,
    minSelections: 1,
    maxSelections: 4,
    countsTowardProgress: true,
  },
  {
    id: 'vibe',
    type: 'multiple',
    eyebrow: "Almost there. What's your style?",
    title: 'What kind of grower are you?',
    options: [
      {
        id: 'classic',
        label: 'Give me the classics everyone loves',
        emoji: '🏆',
        description: 'Tried, true, and impossible to go wrong.',
        tags: ['Classic'],
      },
      {
        id: 'exotic',
        label: 'Show me something rare',
        emoji: '🗺️',
        description: "The tree your neighbors don't have.",
        tags: ['Exotic'],
      },
      {
        id: 'ornamental',
        label: 'I want something beautiful',
        emoji: '✨',
        description: 'A tree that earns compliments.',
        tags: ['Ornamental'],
      },
      {
        id: 'healer',
        label: "I'm into the wellness side",
        emoji: '🧘',
        description: "There's a reason grandma swore by citrus.",
        tags: ['Healer'],
      },
    ],
    allowMultiple: true,
    minSelections: 1,
    maxSelections: 2,
    countsTowardProgress: true,
  },
  {
    id: 'bonus-features',
    type: 'multiple',
    eyebrow: 'Any must-haves?',
    title: 'What would be a bonus for you?',
    helperText: 'Pick all that apply, including none.',
    options: [
      {
        id: 'seedless',
        label: 'No seeds, please',
        emoji: '✅',
        description: "Life's too short for spitting.",
        tags: ['Seedless'],
      },
      {
        id: 'edible-rind',
        label: 'I want to eat the whole fruit — peel and all',
        emoji: '🍊',
        description: "Kumquats are eaten whole. Yes, really. And they're amazing.",
        tags: ['EdibleRind'],
      },
      {
        id: 'cultural',
        label: 'Something with history or cultural roots',
        emoji: '🌍',
        description: 'Citrus has been traded, gifted, and celebrated for 4,000 years.',
        tags: ['Cultural'],
      },
      {
        id: 'surprise',
        label: "Surprise me — I'm open to anything",
        emoji: '🎲',
        tags: [],
      },
    ],
    allowMultiple: true,
    minSelections: 0,
    maxSelections: 4,
    exclusiveOptionIds: ['surprise'],
    countsTowardProgress: true,
  },
  {
    id: 'identity',
    type: 'single',
    autoAdvance: true,
    eyebrow: 'Last one.',
    title: 'What would make you proudest about your tree?',
    options: [
      {
        id: 'family',
        label: 'Picking fruit my family actually eats',
        emoji: '👨‍👩‍👧‍👦',
        description: 'Fresh beats store-bought. Every time.',
        tags: [],
        profileBoosts: PROFILE_NAME_BY_IDENTITY_OPTION.family,
      },
      {
        id: 'kitchen',
        label: 'Making something incredible in the kitchen',
        emoji: '🍽️',
        description: 'Your secret ingredient grows outside.',
        tags: [],
        profileBoosts: PROFILE_NAME_BY_IDENTITY_OPTION.kitchen,
      },
      {
        id: 'rare',
        label: 'Having something no one else has',
        emoji: '🧬',
        description: 'Rare is a lifestyle.',
        tags: [],
        profileBoosts: PROFILE_NAME_BY_IDENTITY_OPTION.rare,
      },
      {
        id: 'wellness',
        label: "Knowing it's good for us",
        emoji: '💚',
        description: "Nature's pharmacy, in your own space.",
        tags: [],
        profileBoosts: PROFILE_NAME_BY_IDENTITY_OPTION.wellness,
      },
      {
        id: 'beauty',
        label: 'How beautiful it looks in my space',
        emoji: '🌿',
        description: 'The tree that earns compliments.',
        tags: [],
        profileBoosts: PROFILE_NAME_BY_IDENTITY_OPTION.beauty,
      },
      {
        id: 'morning',
        label: 'Making fresh juice every morning',
        emoji: '☀️',
        description: 'Your mornings just got an upgrade.',
        tags: [],
        profileBoosts: PROFILE_NAME_BY_IDENTITY_OPTION.morning,
      },
    ],
    countsTowardProgress: true,
  },
  {
    // UX-6: rendered as the staged pre-reveal build-up (CitrusRevealBuildUp),
    // which owns its own ~4s timing and advances itself. No autoAdvanceMs here
    // or the step would advance twice.
    id: 'loading',
    type: 'loading',
    title: 'Putting together your personalized citrus profile...',
    countsTowardProgress: false,
  },
  {
    id: 'email',
    type: 'text',
    title: 'Your results are ready.',
    subtitle: 'Enter your email to unlock:',
    helperText: "No spam — just citrus.",
    placeholder: 'Enter your email',
    buttonLabel: 'Show My Results',
    countsTowardProgress: false,
  },
];

const createEmptyProfileScores = (): Record<ResultProfileId, number> => ({
  'sweet-snacker': 0,
  'culinary-exotic': 0,
  'kitchen-essential': 0,
  'morning-juice-bar': 0,
  'patio-showpiece': 0,
  'holistic-healer': 0,
});

const getQuestionById = (questionId: string): QuizQuestion | undefined =>
  quizQuestions.find((question) => question.id === questionId);

const getOptionById = (questionId: string, optionId: string): QuizOption | undefined =>
  getQuestionById(questionId)?.options?.find((option) => option.id === optionId);

const recomputeAccumulatedTags = (answers: QuizAnswer[]): QuizTag[] =>
  Array.from(new Set(answers.flatMap((answer) => answer.tags))) as QuizTag[];

const getAnswer = (state: QuizState, questionId: string): QuizAnswer | undefined =>
  state.answers.find((answer) => answer.questionId === questionId);

const deterministicIndex = (seed: string, count: number): number => {
  let hash = 0;
  for (let index = 0; index < seed.length; index += 1) {
    hash = (hash << 5) - hash + seed.charCodeAt(index);
    hash |= 0;
  }

  return Math.abs(hash) % count;
};

const getQuestionTags = (questionId: string, selectedOptions: string[]): QuizTag[] =>
  selectedOptions.flatMap((optionId) => getOptionById(questionId, optionId)?.tags ?? []);

const getLocationTag = (state: QuizState): LocationTag =>
  state.accumulatedTags.includes('In-Ground') ? 'In-Ground' : 'Indoor-Patio';

const getFlavorTag = (state: QuizState): FlavorTag => {
  if (state.accumulatedTags.includes('Sour')) return 'Sour';
  if (state.accumulatedTags.includes('Balanced')) return 'Balanced';
  return 'Sweet';
};

const getExperienceValue = (state: QuizState): ExperienceValue => {
  const answerId = getAnswer(state, 'experience')?.selectedOptions[0];
  if (answerId === 'beginner') return 'Beginner';
  if (answerId === 'advanced') return 'Advanced';
  return 'Intermediate';
};

const getUsageTags = (state: QuizState): UsageTag[] =>
  state.accumulatedTags.filter((tag): tag is UsageTag =>
    ['Snack', 'Juice', 'Cook', 'Aromatic'].includes(tag)
  );

const getVibeTags = (state: QuizState): VibeTag[] =>
  state.accumulatedTags.filter((tag): tag is VibeTag =>
    ['Classic', 'Exotic', 'Ornamental', 'Healer'].includes(tag)
  );

const getPrimaryUsageTag = (state: QuizState): UsageTag | null => {
  const usageAnswer = getAnswer(state, 'usage');
  if (!usageAnswer) return null;

  for (const optionId of usageAnswer.selectedOptions) {
    const option = getOptionById('usage', optionId);
    const tag = option?.tags[0];
    if (tag && ['Snack', 'Juice', 'Cook', 'Aromatic'].includes(tag)) {
      return tag as UsageTag;
    }
  }

  return null;
};

const calculateProfileScores = (state: QuizState) => {
  const baseScores = createEmptyProfileScores();
  const identityScores = createEmptyProfileScores();

  [...getUsageTags(state), ...getVibeTags(state)].forEach((tag) => {
    const weights = PROFILE_BASE_WEIGHTS[tag];
    Object.entries(weights).forEach(([profileId, points]) => {
      baseScores[profileId as ResultProfileId] += points ?? 0;
    });
  });

  const identityAnswer = getAnswer(state, 'identity');
  identityAnswer?.selectedOptions.forEach((optionId) => {
    const option = getOptionById('identity', optionId);
    Object.entries(option?.profileBoosts ?? {}).forEach(([profileId, points]) => {
      identityScores[profileId as ResultProfileId] += points ?? 0;
    });
  });

  const totalScores = createEmptyProfileScores();
  PROFILE_ORDER.forEach((profileId) => {
    totalScores[profileId] = baseScores[profileId] + identityScores[profileId];
  });

  return { baseScores, identityScores, totalScores };
};

const selectWinningProfile = (
  state: QuizState,
  totalScores: Record<ResultProfileId, number>,
  identityScores: Record<ResultProfileId, number>
): ResultProfileId => {
  const highestScore = Math.max(...PROFILE_ORDER.map((profileId) => totalScores[profileId]));
  const tiedProfiles = PROFILE_ORDER.filter((profileId) => totalScores[profileId] === highestScore);

  if (tiedProfiles.length === 1) {
    return tiedProfiles[0];
  }

  const highestIdentityScore = Math.max(...tiedProfiles.map((profileId) => identityScores[profileId]));
  const identityWinners = tiedProfiles.filter((profileId) => identityScores[profileId] === highestIdentityScore);

  if (identityWinners.length === 1) {
    return identityWinners[0];
  }

  const flavorWinner = PROFILE_FALLBACK_BY_FLAVOR[getFlavorTag(state)];
  if (identityWinners.includes(flavorWinner)) {
    return flavorWinner;
  }

  return identityWinners[0];
};

const getPrimaryArchetype = (state: QuizState): string => {
  for (const tag of ARCHETYPE_PRIORITY) {
    if (state.accumulatedTags.includes(tag) && ARCHETYPE_BY_PRIORITY[tag]) {
      return ARCHETYPE_BY_PRIORITY[tag] as string;
    }
  }

  return 'The Everyday Epicurean';
};

const selectHeadline = (
  profileId: ResultProfileId,
  state: QuizState,
  archetype: string,
  location: LocationTag
): string => {
  const variants = HEADLINE_VARIANTS[profileId];
  const usageTags = getUsageTags(state);
  const vibeTags = getVibeTags(state);
  const primaryUsage = getPrimaryUsageTag(state);

  switch (profileId) {
    case 'sweet-snacker':
      if (['The Everyday Epicurean', 'The Home Chef', 'The Legacy Builder'].includes(archetype)) {
        return variants.variantA;
      }
      if (['The Trailblazer', 'The First-Timer', 'The Urban Cultivator'].includes(archetype)) {
        return variants.variantB;
      }
      return variants.default;
    case 'culinary-exotic':
      if (usageTags.includes('Cook')) {
        return variants.variantA;
      }
      if (vibeTags.includes('Exotic') && usageTags.includes('Aromatic')) {
        return variants.variantB;
      }
      return variants.default;
    case 'kitchen-essential':
      if (primaryUsage === 'Cook') {
        return variants.variantA;
      }
      if (vibeTags.includes('Classic') && usageTags.includes('Cook')) {
        return variants.variantB;
      }
      return variants.default;
    case 'patio-showpiece':
      return location === 'Indoor-Patio' ? variants.variantA : variants.variantB;
    case 'holistic-healer':
      return location === 'Indoor-Patio' ? variants.variantA : variants.variantB;
    case 'morning-juice-bar':
      if (usageTags.length === 1 && usageTags[0] === 'Juice') {
        return variants.variantA;
      }
      if (usageTags.includes('Juice') && usageTags.includes('Snack')) {
        return variants.variantB;
      }
      return variants.default;
    default:
      return variants.default;
  }
};

const selectKeyInsight = (profileId: ResultProfileId, state: QuizState): string => {
  const options = KEY_INSIGHTS_BY_PROFILE[profileId];
  const seed = `${profileId}:${state.answers.map((answer) => `${answer.questionId}:${answer.selectedOptions.join(',')}`).join('|')}`;
  return options[deterministicIndex(seed, options.length)];
};

const selectBlindSpot = (profileId: ResultProfileId, archetype: string): string =>
  BLIND_SPOTS[profileId][archetype] ?? BLIND_SPOTS[profileId].Any;

const getSpiritCitrus = (recommendations: TreeScore[]): string =>
  recommendations[0]?.tree.name ?? 'Meyer Lemon Tree';

const getCitrusType = (tree: CitrusTree): string => {
  const name = tree.name.toLowerCase();

  if (name.includes('kumquat')) return 'kumquat';
  if (name.includes('mandarin') || name.includes('tangerine') || name.includes('satsuma')) return 'mandarin';
  if (name.includes('grapefruit')) return 'grapefruit';
  if (name.includes('orange') || name.includes('tangelo')) return 'orange';
  if (name.includes('lime')) return 'lime';
  if (name.includes('lemon')) return 'lemon';

  return `other-${tree.id}`;
};

const limitByCitrusType = (scoredTrees: TreeScore[], maxPerType: number, targetCount: number): TreeScore[] => {
  const counts: Record<string, number> = {};
  const selected: TreeScore[] = [];

  for (const treeScore of scoredTrees) {
    const citrusType = getCitrusType(treeScore.tree);
    const isLimitedType = !citrusType.startsWith('other-');
    const currentCount = counts[citrusType] ?? 0;

    if (!isLimitedType || currentCount < maxPerType) {
      selected.push(treeScore);
      counts[citrusType] = currentCount + 1;
    }

    if (selected.length === targetCount) {
      break;
    }
  }

  if (selected.length < targetCount) {
    const selectedIds = new Set(selected.map((item) => item.tree.id));
    const backfill = scoredTrees.filter((item) => !selectedIds.has(item.tree.id));
    return [...selected, ...backfill].slice(0, targetCount);
  }

  return selected;
};

/**
 * Keep sold-out trees out of the recommendation pool when live Shopify metadata
 * says they are unavailable.
 * Why this matters: Ron's requirement is strict: if Shopify marks a tree as sold out,
 * the quiz should not recommend it. Trees without live availability metadata remain
 * eligible so a sync outage does not blank out the results.
 */
const isRecommendationEligible = (tree: CitrusTree): boolean => tree.isAvailable !== false;

/**
 * UX-2: which because-line triggers fired for this tree, in strength order.
 * A trigger fires when the person picked the tag AND the tree satisfies it
 * (the same conditions scoreTree rewards).
 */
const matchedBecauseTags = (tree: CitrusTree, tags: QuizTag[]): string[] => {
  const satisfies: Record<string, boolean> = {
    'Indoor-Patio': tree.growingLocation.includes('indoor') || tree.growingLocation.includes('patio'),
    'In-Ground': tree.growingLocation.includes('in-ground'),
    Beginner: tree.difficulty === 'beginner',
    Advanced: tree.difficulty === 'advanced',
    Sweet: tree.flavorProfile.includes('sweet'),
    Sour: tree.flavorProfile.includes('sour'),
    Balanced: tree.flavorProfile.includes('balanced'),
    Snack: tree.primaryUse.includes('snack'),
    Juice: tree.primaryUse.includes('juice'),
    Cook: tree.primaryUse.includes('cook'),
    Aromatic: tree.primaryUse.includes('aromatic') || tree.primaryUse.includes('ornamental'),
    Classic: tree.personality.includes('classic'),
    Exotic: tree.personality.includes('exotic'),
    Ornamental: tree.personality.includes('aesthete'),
    Healer: tree.personality.includes('healer'),
    Seedless: tree.tags.includes('Seedless Variety'),
    EdibleRind: Boolean(tree.edibleRind),
    Cultural: Boolean(tree.culturalSignificance),
  };

  return CITRUS_BECAUSE_PRIORITY.filter(
    (tag) => tags.includes(tag as QuizTag) && satisfies[tag]
  );
};

/**
 * UX-2: assign one "Because [your answer], [this tree fits]" line per card,
 * in render order. The hero (#1 match) stacks its two strongest triggers into
 * one sentence; other cards rotate triggers so no two neighbors repeat one.
 */
export const assignBecauseLines = (scoredTrees: TreeScore[], tags: QuizTag[]): TreeScore[] => {
  let previousId: string | null = null;

  return scoredTrees.map((treeScore, index) => {
    const templates: CitrusBecauseTemplate[] = matchedBecauseTags(treeScore.tree, tags)
      .map((tag) => CITRUS_BECAUSE_BY_TAG[tag])
      .filter((template): template is CitrusBecauseTemplate => Boolean(template));

    const primary = templates.find((template) => template.id !== previousId);
    let line: string;

    if (index === 0 && primary) {
      const secondary = templates.find((template) => template.id !== primary.id);
      line = secondary ? citrusBecauseLineStacked(primary, secondary) : citrusBecauseLine(primary);
    } else if (primary) {
      line = citrusBecauseLine(primary);
    } else {
      line = citrusBecauseLine(CITRUS_BECAUSE_FALLBACK);
    }

    previousId = primary ? primary.id : CITRUS_BECAUSE_FALLBACK.id;
    return { ...treeScore, personalizedReason: line };
  });
};

const scoreTree = (tree: CitrusTree, tags: QuizTag[]): TreeScore => {
  let score = 0;
  const matchReasons: string[] = [];

  if (tags.includes('Indoor-Patio')) {
    if (tree.growingLocation.includes('indoor') || tree.growingLocation.includes('patio')) {
      score += 30;
      matchReasons.push('Fits indoor or patio growing');
    } else {
      score -= 50;
    }
  }

  if (tags.includes('In-Ground')) {
    if (tree.growingLocation.includes('in-ground')) {
      score += 30;
      matchReasons.push('Thrives in the ground');
    } else if (!tree.growingLocation.includes('indoor') && !tree.growingLocation.includes('patio')) {
      score -= 15;
    }
  }

  if (tags.includes('Beginner')) {
    if (tree.difficulty === 'beginner') {
      score += 25;
      matchReasons.push('Beginner-friendly');
    } else if (tree.difficulty === 'advanced') {
      score -= 20;
    }
  }

  if (tags.includes('Advanced')) {
    if (tree.difficulty === 'advanced') {
      score += 25;
      matchReasons.push('Worth the challenge');
    } else {
      score += 8;
    }
  }

  if (tags.includes('Sweet') && tree.flavorProfile.includes('sweet')) {
    score += 20;
    matchReasons.push('Matches your sweet preference');
  }

  if (tags.includes('Sour') && tree.flavorProfile.includes('sour')) {
    score += 20;
    matchReasons.push('Brings the tart punch you asked for');
  }

  if (tags.includes('Balanced') && tree.flavorProfile.includes('balanced')) {
    score += 20;
    matchReasons.push('Delivers a sweet-tart balance');
  }

  if (tags.includes('Snack') && tree.primaryUse.includes('snack')) {
    score += 15;
    matchReasons.push('Great for eating fresh');
  }

  if (tags.includes('Juice') && tree.primaryUse.includes('juice')) {
    score += 15;
    matchReasons.push('Strong juicing pick');
  }

  if (tags.includes('Cook') && tree.primaryUse.includes('cook')) {
    score += 15;
    matchReasons.push('Useful in the kitchen');
  }

  if (tags.includes('Aromatic') && (tree.primaryUse.includes('aromatic') || tree.primaryUse.includes('ornamental'))) {
    score += 15;
    matchReasons.push('Adds fragrance and visual appeal');
  }

  if (tags.includes('Classic') && tree.personality.includes('classic')) {
    score += 10;
    matchReasons.push('A proven classic');
  }

  if (tags.includes('Exotic') && tree.personality.includes('exotic')) {
    score += 10;
    matchReasons.push('A rarer, more interesting variety');
  }

  if (tags.includes('Ornamental') && tree.personality.includes('aesthete')) {
    score += 10;
    matchReasons.push('Looks as good as it tastes');
  }

  if (tags.includes('Healer') && tree.personality.includes('healer')) {
    score += 10;
    matchReasons.push('Leans into the wellness side of citrus');
  }

  if (tags.includes('Seedless') && tree.tags.includes('Seedless Variety')) {
    score += 20;
    matchReasons.push('Checks your seedless box');
  }

  if (tags.includes('EdibleRind') && tree.edibleRind) {
    score += 25;
    matchReasons.push('You can eat the whole fruit');
  }

  if (tags.includes('Cultural') && tree.culturalSignificance) {
    score += 10;
    matchReasons.push('Has the story and heritage you wanted');
  }

  if (tree.reviews > 100) {
    score += 10;
  } else if (tree.reviews > 50) {
    score += 5;
  }

  return {
    tree,
    score,
    matchReasons,
    // Placeholder; getRecommendations assigns the final because line so
    // rotation and hero stacking can consider the whole rendered list.
    personalizedReason: matchReasons[0] ?? tree.shortDescription,
  };
};

export const processAnswer = (
  state: QuizState,
  questionId: string,
  selectedOptions: string[],
  textValue?: string
): QuizState => {
  const question = getQuestionById(questionId);
  if (!question) return state;

  if (question.type === 'text') {
    if (questionId === 'email') {
      return {
        ...state,
        email: textValue ?? '',
      };
    }

    if (questionId === 'welcome') {
      return {
        ...state,
        userName: textValue ?? '',
      };
    }

    if (questionId === 'zipcode') {
      return {
        ...state,
        zipCode: textValue ?? '',
      };
    }
  }

  const sanitizedSelections = [...selectedOptions];
  const exclusiveOptionIds = question.exclusiveOptionIds ?? [];

  if (exclusiveOptionIds.length > 0) {
    const selectedExclusive = sanitizedSelections.filter((optionId) => exclusiveOptionIds.includes(optionId));
    if (selectedExclusive.length > 0) {
      const mostRecentExclusive = selectedExclusive[selectedExclusive.length - 1];
      if (sanitizedSelections.length > 1) {
        return processAnswer(state, questionId, [mostRecentExclusive], textValue);
      }
    }
  }

  const nextAnswer: QuizAnswer = {
    questionId,
    selectedOptions: sanitizedSelections,
    tags: getQuestionTags(questionId, sanitizedSelections),
  };

  const nextAnswers = [
    ...state.answers.filter((answer) => answer.questionId !== questionId),
    nextAnswer,
  ];

  return {
    ...state,
    answers: nextAnswers,
    accumulatedTags: recomputeAccumulatedTags(nextAnswers),
  };
};

export const nextStep = (state: QuizState): QuizState => ({
  ...state,
  currentStep: Math.min(state.currentStep + 1, quizQuestions.length - 1),
});

export const prevStep = (state: QuizState): QuizState => {
  let previousStep = Math.max(0, state.currentStep - 1);

  while (previousStep > 0) {
    const previousQuestion = quizQuestions[previousStep];
    const isSkippableScreen = previousQuestion.type === 'screen' && Boolean(
      previousQuestion.autoAdvanceMs || !previousQuestion.buttonLabel
    );

    if (previousQuestion.type !== 'loading' && !isSkippableScreen) {
      break;
    }
    previousStep -= 1;
  }

  return {
    ...state,
    currentStep: previousStep,
  };
};

export const getRecommendations = (
  state: QuizState,
  treeCatalog: CitrusTree[] = citrusTrees
): TreeScore[] => {
  const tags = state.accumulatedTags;
  const scoredTrees = treeCatalog
    .filter(isRecommendationEligible)
    .map((tree) => scoreTree(tree, tags))
    .sort((left, right) => right.score - left.score);

  const minimumScoreThreshold = 10;
  const primaryMatches = scoredTrees.filter((treeScore) => treeScore.score >= minimumScoreThreshold);
  const secondaryMatches = scoredTrees.filter(
    (treeScore) => treeScore.score < minimumScoreThreshold && treeScore.score > 0
  );

  const cappedPrimaryMatches = limitByCitrusType(primaryMatches, 2, 16);
  if (cappedPrimaryMatches.length >= 16) {
    return assignBecauseLines(cappedPrimaryMatches.slice(0, 16), tags);
  }

  const backfilled = [...cappedPrimaryMatches];
  const selectedIds = new Set(backfilled.map((treeScore) => treeScore.tree.id));
  secondaryMatches.forEach((treeScore) => {
    if (!selectedIds.has(treeScore.tree.id) && backfilled.length < 16) {
      backfilled.push(treeScore);
    }
  });

  return assignBecauseLines(backfilled.slice(0, 16), tags);
};

export const getResultProfile = (state: QuizState): ResultProfile => {
  const { totalScores, identityScores } = calculateProfileScores(state);
  const profileId = selectWinningProfile(state, totalScores, identityScores);
  const archetype = getPrimaryArchetype(state);
  const headline = selectHeadline(profileId, state, archetype, getLocationTag(state));

  return {
    ...RESULT_PROFILES[profileId],
    headline,
  };
};

export const resolveQuizResult = (
  state: QuizState,
  providedRecommendations?: TreeScore[]
): ResolvedQuizResult => {
  const recommendations = providedRecommendations ?? getRecommendations(state);
  const { totalScores, identityScores } = calculateProfileScores(state);
  const profileId = selectWinningProfile(state, totalScores, identityScores);
  const archetype = getPrimaryArchetype(state);
  const location = getLocationTag(state);
  const headline = selectHeadline(profileId, state, archetype, location);
  const topMatches = recommendations.slice(0, Math.min(6, recommendations.length));
  const moreMatches = recommendations.slice(topMatches.length);
  const profile = {
    ...RESULT_PROFILES[profileId],
    headline,
  };

  return {
    profile,
    profileScores: totalScores,
    identityScores,
    headline,
    archetype,
    coreReading: CORE_READINGS[profileId][location],
    superpower: SUPERPOWERS_BY_ARCHETYPE[archetype] ?? "You know exactly what matters and you build around it.",
    keyInsight: selectKeyInsight(profileId, state),
    blindSpot: selectBlindSpot(profileId, archetype),
    spiritCitrus: getSpiritCitrus(recommendations),
    topMatches,
    moreMatches,
    recommendations,
    location,
    flavor: getFlavorTag(state),
    experience: getExperienceValue(state),
  };
};

export const generatePersonalityReading = (
  stateOrTags: QuizState | QuizTag[],
  recommendations: TreeScore[]
): PersonalityReading => {
  const state: QuizState = Array.isArray(stateOrTags)
    ? {
        ...initialQuizState,
        accumulatedTags: stateOrTags,
      }
    : stateOrTags;

  const result = resolveQuizResult(state, recommendations);
  return {
    archetype: result.archetype,
    rarityPercentage: null,
    coreReading: result.coreReading,
    strengthLine: result.superpower,
    keyInsightLine: result.keyInsight,
    blindSpotLine: result.blindSpot,
    citrusSpirit: result.spiritCitrus,
  };
};

/**
 * Build Ibrahim's Klaviyo email fields from resolved quiz content.
 * Why this matters: emails use merge tags only; no Klaviyo conditional blocks.
 */
export const buildQuizEmailContent = async (
  result: ResolvedQuizResult,
  recommendations: TreeScore[]
): Promise<QuizEmailContent> => {
  const { getTreeLongSnippet, getTreeShortSnippet } = await loadTreeSnippetResolvers();

  const shortSnippetForSlot = (index: number): string => {
    const treeScore = recommendations[index];
    if (!treeScore) return '';
    return getTreeShortSnippet(treeScore.tree.id, treeScore.tree.name);
  };

  const longSnippetForSlot = (index: number): string => {
    const treeScore = recommendations[index];
    if (!treeScore) return '';
    return getTreeLongSnippet(treeScore.tree.id, treeScore.tree.name);
  };

  return {
    citrus_quiz_archetype_paragraph: `You are ${result.archetype}.\n\n${result.coreReading}`,
    citrus_quiz_profile_description: result.profile.description,
    citrus_quiz_headline: result.headline,
    citrus_quiz_superpower: result.superpower,
    citrus_quiz_key_insight: result.keyInsight,
    citrus_quiz_blind_spot: result.blindSpot,
    citrus_quiz_spirit_citrus: result.spiritCitrus,
    citrus_tree_1_snippet: shortSnippetForSlot(0),
    citrus_tree_2_snippet: shortSnippetForSlot(1),
    citrus_tree_3_snippet: shortSnippetForSlot(2),
    citrus_tree_1_snippet_long: longSnippetForSlot(0),
    citrus_tree_2_snippet_long: longSnippetForSlot(1),
    citrus_tree_3_snippet_long: longSnippetForSlot(2),
    citrus_tree_4_snippet: shortSnippetForSlot(3),
    citrus_tree_5_snippet: shortSnippetForSlot(4),
    citrus_tree_6_snippet: shortSnippetForSlot(5),
  };
};

export const buildKlaviyoPayload = async (
  state: QuizState,
  recommendations: TreeScore[]
): Promise<KlaviyoQuizPayload> => {
  const result = resolveQuizResult(state, recommendations);
  const answers: Record<string, string[]> = {};
  const emailContent = await buildQuizEmailContent(result, recommendations);

  state.answers.forEach((answer) => {
    answers[answer.questionId] = answer.selectedOptions;
  });

  return {
    email: state.email,
    firstName: state.userName,
    zipCode: state.zipCode,
    quizProfile: result.profile.name,
    quizProfileId: result.profile.id,
    quizArchetype: result.archetype,
    quizLocation: result.location,
    quizFlavor: result.flavor,
    quizExperience: result.experience,
    recommendedTrees: recommendations.map((recommendation) => ({
      id: recommendation.tree.id,
      name: recommendation.tree.name,
      productUrl: recommendation.tree.productUrl,
      imageUrl: recommendation.tree.imageUrl,
    })),
    tags: state.accumulatedTags,
    answers,
    emailContent,
  };
};
